"use client";

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from "next-intl";
import { Icon, MapPinIcon, TextButton, Typography } from "@/shared/ui/atoms";
import { toast } from "@/shared/utils/toastMessage";
import HospitalHomepageLink from "@/widgets/hospital-detail/ui/molecules/HospitalHomepageLink";
import { DemandSignalButtons } from "@/features/demand-signal";
interface HospitalInfoPanelProps {
  tel: string;
  fullAddress: string;
  homepageUrl?: string;
  closed?: boolean;
  suspended?: boolean;
  /** 수요 조사 파일럿 병원이면 여기에도 버튼을 둔다(B2C-475). 데스크톱에는 하단 고정 바가 없다. */
  demandSignalHospitalOriginId?: number;
}

/**
 * 데스크톱 오른쪽 열에 계속 붙어 있는 정보판.
 *
 * 본문을 어디까지 읽었든 "전화번호·주소·오늘 진료시간"은 눈에 남아 있어야 한다 —
 * 개편 전에는 이 셋이 본문 맨 위에만 있어서 리뷰를 읽다 보면 화면 밖으로 사라졌다.
 * 화면이 좁을 때는 히어로와 위치 섹션이 같은 정보를 이미 들고 있으므로 숨긴다.
 */
const HospitalInfoPanel = t0 => {
  const $ = _c(36);
  const {
    tel,
    fullAddress,
    homepageUrl,
    closed: t1,
    suspended: t2,
    demandSignalHospitalOriginId
  } = t0;
  const closed = t1 === undefined ? false : t1;
  const suspended = t2 === undefined ? false : t2;
  const t = useTranslations();
  const isNonOperating = closed || suspended;
  let t3;
  if ($[0] !== fullAddress || $[1] !== t) {
    t3 = async () => {
      try {
        await navigator.clipboard.writeText(fullAddress);
        toast.success(t("common.copySuccess"));
      } catch {
        toast.error(t("common.copyLinkFailed"));
      }
    };
    $[0] = fullAddress;
    $[1] = t;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const handleCopyAddress = t3;
  let t4;
  if ($[3] !== t) {
    t4 = t("contact.title");
    $[3] = t;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t4) {
    t5 = <Typography as="h2" size={15} lineHeight={20} className="font-bold text-gray-900">{t4}</Typography>;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== isNonOperating || $[8] !== tel) {
    t6 = !isNonOperating && tel ? <a href={`tel:${tel}`} className="flex items-center gap-2 text-gray-700 hover:text-primary-700"><Icon src="/icons/call.svg" alt="" width={16} height={16} /><Typography as="span" size={14} lineHeight={20} className="tabular-nums">{tel}</Typography></a> : null;
    $[7] = isNonOperating;
    $[8] = tel;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <MapPinIcon size={16} className="mt-0.5 flex-shrink-0 text-gray-300" />;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== fullAddress) {
    t8 = <Typography as="span" size={14} lineHeight={20} className="flex-1 text-gray-700 break-keep">{fullAddress}</Typography>;
    $[11] = fullAddress;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t) {
    t9 = t("common.copy");
    $[13] = t;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== handleCopyAddress || $[16] !== t9) {
    t10 = <TextButton text={t9} size={12} className="flex-shrink-0 text-gray-500 leading-[18px] underline decoration-gray-500 underline-offset-[3px]" onClick={handleCopyAddress} />;
    $[15] = handleCopyAddress;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10 || $[19] !== t8) {
    t11 = <div className="flex items-start gap-2">{t7}{t8}{t10}</div>;
    $[18] = t10;
    $[19] = t8;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== homepageUrl || $[22] !== isNonOperating || $[23] !== t) {
    t12 = !isNonOperating ? <HospitalHomepageLink homepageUrl={homepageUrl} label={t("contact.homepage")} /> : null;
    $[21] = homepageUrl;
    $[22] = isNonOperating;
    $[23] = t;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== t11 || $[26] !== t12 || $[27] !== t5 || $[28] !== t6) {
    t13 = <section className="flex flex-col gap-2.5 border-t border-gray-100 pt-4 first:border-t-0 first:pt-0">{t5}{t6}{t11}{t12}</section>;
    $[25] = t11;
    $[26] = t12;
    $[27] = t5;
    $[28] = t6;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== demandSignalHospitalOriginId || $[31] !== isNonOperating) {
    t14 = !isNonOperating && demandSignalHospitalOriginId ? <section className="flex flex-col gap-2.5 border-t border-gray-100 pt-4 first:border-t-0 first:pt-0"><DemandSignalButtons hospitalOriginId={demandSignalHospitalOriginId} variant="panel" /></section> : null;
    $[30] = demandSignalHospitalOriginId;
    $[31] = isNonOperating;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== t13 || $[34] !== t14) {
    t15 = <div className="flex flex-col gap-4 rounded-2xl border border-gray-100 bg-white p-5">{t13}{t14}</div>;
    $[33] = t13;
    $[34] = t14;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  return t15;
};
export default HospitalInfoPanel;